<template>
  <v-layout row justify-center>
    <v-dialog v-model="createDialog" fullscreen transition="dialog-bottom-transition" :overlay="false">
      <v-card class="elevation-1 processing-record-details">
        <v-layout column justify-space-between>
          <v-layout column>
            <!-- Modal header -->
            <v-toolbar color="secondary white--text">
              <v-toolbar-title class="subheading ttu">
                <span>{{ messages.label.CREATE_PROCESSING_RECORD }}</span>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-toolbar-items>
                <v-btn @click.stop="hide()" flat>
                  <v-icon class="white--text">close</v-icon>
                </v-btn>
              </v-toolbar-items>
            </v-toolbar>
            <!-- Main Content -->
            <v-card-text>
              <!-- Provider Group Selection -->
              <v-flex xs3>
                <v-autocomplete label="Provider Group" v-model="selectedProviderGroup" item-text="title"
                  :loading="loadingProviderGroups" v-validate="'required'"
                  :error-messages="errors.collect('providerGroup')" data-vv-name="providerGroup"
                  :items="providerGroups">
                  <template v-slot:no-data>
                    <v-list-tile>
                      <v-list-tile-title>
                        No Provider groups in
                        <strong>{{ mpxParentAccount.title }}</strong>
                      </v-list-tile-title>
                    </v-list-tile>
                  </template>
                </v-autocomplete>
              </v-flex>
              <!-- XML Editor -->
              <h6 class="subheading pa-1">ADI XML</h6>
              <v-alert color="error" icon="warning" v-model="errorAlert" dismissible>
                <span v-html="ADIValidationError"></span>
              </v-alert>
              <v-flex xs12 v-if="responseMsg !== ''">
                <v-alert color="success" icon="check_circle" value="true" v-if="processingRecordSuccess">
                  <span v-html="responseMsg"></span>
                </v-alert>
                <v-alert color="error" icon="warning" v-model="failureAlert" dismissible
                  v-if="!processingRecordSuccess">
                  <span v-html="responseMsg"></span>
                </v-alert>
              </v-flex>
              <v-card :height="editorHeight" class="elevation-1">
                <div ref="editorEditable" class="vop--ace-editor"></div>
              </v-card>
            </v-card-text>
          </v-layout>
          <v-card-actions class="grey lighten-4 py-2">
            <v-flex xs12 v-if="processingRecordSuccess" class="pt-2">
              <v-btn color="primary" @click="hide()">
                {{ messages.label.OK }}
              </v-btn>
            </v-flex>
            <v-flex xs12 v-if="!processingRecordSuccess" class="pt-2">
              <v-btn color="primary" :disabled="XMLSytaxError" :loading="savingADI" @click="validate()">
                {{ messages.label.CREATE }}
              </v-btn>
              <v-btn color="grey lighten-1 white--text" @click="hide()">
                {{ messages.label.CANCEL }}
              </v-btn>
            </v-flex>
          </v-card-actions>
        </v-layout>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import ace from "brace"
import "brace/mode/xml"
import "brace/theme/eclipse"
import "brace/ext/searchbox"
import PreIngestService from "@/PreIngest/PreIngestService"
import * as preIngestDataService from "@/PreIngest/PreIngestDataService"
import messages from "@/PreIngest/messages.json"
import { VOD_SYCN_PREINGEST_WEB_SERVICE_URL } from "@/store/constants/getter-types"
import { GET_PROVIDER_GROUPS } from "@/store/constants/action-types"

export default {
  name: "CreateProcessingRecord",
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return {
      createDialog: false,
      ADIValidationError: "",
      XMLSytaxError: false,
      errorAlert: false,
      failureAlert: false,
      savingADI: false,
      processingRecordSuccess: false,
      responseMsg: "",
      loadingProviderGroups: false,
      selectedProviderGroup: "",
      dictionary: this.getValidationDictionary()
    }
  },
  computed: {
    ...mapState(["providerGroups", "mpxParentAccount"]),
    ...mapGetters([VOD_SYCN_PREINGEST_WEB_SERVICE_URL]),
    editorHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          if (this.responseMsg === "" && !this.errorAlert) {
            return "72vh"
          } else {
            return "65vh"
          }
        case "sm":
          if (this.responseMsg === "" && !this.errorAlert) {
            return "72vh"
          } else {
            return "65vh"
          }
        case "md":
          if (this.responseMsg === "" && !this.errorAlert) {
            return "75vh"
          } else {
            return "68vh"
          }
        case "lg":
          if (this.responseMsg === "" && !this.errorAlert) {
            return "75vh"
          } else {
            return "68vh"
          }
        case "xl":
          if (this.responseMsg === "" && !this.errorAlert) {
            return "87vh"
          } else {
            return "80vh"
          }
      }
    }
  },
  methods: {
    ...mapActions([GET_PROVIDER_GROUPS]),

    /**
     * Hide Create Processing Record Modal
     */
    hide() {
      this.resetData()
      this.createDialog = false
    },

    /**
     * Show Create Processing Record Modal
     */
    show() {
      this.resetData()
      this.createDialog = true
      this.openXMLEditor()
    },

    /**
     * Open XML Editor
     */
    openXMLEditor() {
      let editor
      editor = ace.edit(this.$refs.editorEditable)
      editor.setTheme("ace/theme/eclipse")
      editor.getSession().setMode("ace/mode/xml")
      editor.session.setValue("")
      this.$refs.editorEditable.style.backgroundColor = "white"
      editor.setReadOnly(false)
      editor.getSession().on("changeAnnotation", event => {
        this.validateXMLEditor(event, editor)
      })
    },

    /**
     * Check if XML is in valid state, if not disable 'SAVE' button
     */
    validateXMLEditor(event, editor) {
      let xmlError = false
      let annotationLists = editor.getSession().getAnnotations()
      for (var i in annotationLists) {
        let annotationError = annotationLists[i].type
        if (annotationError === "error" || annotationError === "warning") {
          xmlError = true
          break
        }
      }
      if (xmlError) {
        this.XMLSytaxError = true
      } else {
        this.XMLSytaxError = false
      }
    },

    /**
     * Validate if XML contains all the required fields or not
     */
    validate() {
      this.$validator.validateAll().then(result => {
        if (result) {
          let editor = ace.edit(this.$refs.editorEditable)
          let xmlString = editor.getValue()
          // XML validation
          if (!this.$lodash.isEmpty(xmlString)) {
            let parser = new DOMParser()
            let xmlDocument = parser.parseFromString(xmlString, "text/xml")
            let AMSTagsArray = xmlDocument.getElementsByTagName("AMS")
            if (!this.$lodash.isEmpty(AMSTagsArray)) {
              for (let AMCTag of AMSTagsArray) {
                if (
                  AMCTag.hasAttribute("Provider") &&
                  !this.$lodash.isEmpty(AMCTag.getAttribute("Provider"))
                ) {
                  if (
                    AMCTag.hasAttribute("Provider_ID") &&
                    !this.$lodash.isEmpty(AMCTag.getAttribute("Provider_ID"))
                  ) {
                    if (
                      AMCTag.hasAttribute("Version_Major") &&
                      !this.$lodash.isEmpty(
                        AMCTag.getAttribute("Version_Major")
                      )
                    ) {
                      this.errorAlert = false
                      this.ADIValidationError = ""
                    } else {
                      this.errorAlert = true
                      this.ADIValidationError =
                        messages.error.MISSING_VERSION_MAJOR
                      break
                    }
                  } else {
                    this.errorAlert = true
                    this.ADIValidationError =
                      messages.error.MISSING_PROVIDER_ID
                    break
                  }
                } else {
                  this.errorAlert = true
                  this.ADIValidationError = messages.error.MISSING_PROVIDER
                  break
                }
              }
            } else {
              this.errorAlert = true
              this.ADIValidationError = messages.error.INVALID_METADATAXML
            }
          } else {
            this.errorAlert = true
            this.ADIValidationError = messages.error.NO_METADATAXML
          }
          // Create processing record if no validation errors
          if (this.ADIValidationError === "") {
            this.createProcessingRecord(xmlString, editor)
          }
        }
      })
    },

    /**
     * Make API call for creating processing record
     */
    createProcessingRecord(xmlString, editor) {
      this.savingADI = true
      let reqBody = {
        addMetadata: {
          metadataXml: xmlString
        }
      }
      preIngestDataService
        .createProcessingRecord(
          reqBody,
          this.selectedProviderGroup.ownerId,
          this.VOD_SYCN_PREINGEST_WEB_SERVICE_URL
        )
        .then(() => {
          this.processingRecordSuccess = true
          editor.setReadOnly(true)
          this.$refs.editorEditable.style.backgroundColor = "floralwhite"
          this.responseMsg = this.getSuccessMsg()
          this.savingADI = false
        })
        .catch(error => {
          let response = error.response.data
          this.savingADI = false
          this.processingRecordSuccess = false
          this.responseMsg = this.getFailureMsg(response)
          this.failureAlert = true
        })
    },

    /**
     * Construct Create Processing Record success message
     */
    getSuccessMsg() {
      let successMsg = messages.info.CREATE_PROCESSING_RECORD_SUCCESS
      return successMsg
    },

    /**
     * Construct Create Processing Record failure message
     */
    getFailureMsg(response) {
      let correlationId = response.correlationId
      let description = response.description
      let failureMsg = messages.error.CREATE_PROCESSING_RECORD_FAILURE
      failureMsg +=
        "<br/>" +
        messages.label.REASON +
        this.$lodash.head(description.split(":"))
      failureMsg += "<br/>" + messages.label.CORRELATION_ID + correlationId
      return failureMsg
    },

    /**
     * Re-initialize
     */
    resetData() {
      this.ADIValidationError = ""
      this.XMLSytaxError = false
      this.errorAlert = false
      this.savingADI = false
      this.processingRecordSuccess = false
      this.responseMsg = ""
      this.failureAlert = false
      this.selectedProviderGroup = ""
      this.$validator.reset()
    },

    /**
     * Validation messages for vee-validate
     */
    getValidationDictionary() {
      return {
        custom: {
          providerGroup: {
            required: () => "Please select provider group"
          }
        }
      }
    },

    /**
     * Get the provider groups list
     */
    retrieveProviderGroups() {
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS()
        .catch(() => {
          // TODO: handle error
        })
        .finally(() => {
          this.loadingProviderGroups = false
        })
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    //  Retrieve mpx token
    this.retrieveProviderGroups()

    /**
     * Events
     */
    PreIngestService.$on("event:createProcessingRecord", () => {
      this.resetData()
      this.show()
    })
  },
  mounted() {
    this.$validator.localize("en", this.dictionary)
  }
}
</script>

<style scoped>
.vop--ace-editor {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 20px;
  right: 20px;
}

.vop--ace-editor .ace_search_field,
.ace_searchbtn:last-child {
  color: black !important;
}

.processing-record-details {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>



// WEBPACK FOOTER //
// src/PreIngest/ProcessingRecords/CreateProcessingRecord.vue